import { useEffect } from 'react';
import { Frame } from './Frame';
import { RoadMap, type RoadStop } from './RoadMap';
import { DepthRoad } from './DepthRoad';
import { getChallenge } from '../data/exercises';
import { PER_CHAPTER, chaptersFor, goalFor } from '../data/road';
import { IMAGINE, ROAD, fillIn } from '../data/imagine-copy';
import { loadProfile } from '../lib/state';
import { track } from '../analytics';

/**
 * The road, in the intro, for people an ad sent with `?start=imagine`
 * (well/product/33, owners 2026-10-14/15). It follows the first card ("Make
 * one small thing a day.") in place of the two after it: the promise said,
 * then shown as the road they are about to walk, as big as the phone allows.
 *
 * It is the Road tab's own map, chapter one, ending at what they just said
 * they would like to be. Today's stop is their first creation, lit, with
 * Kraben waving at it; the chapter's stops follow from tomorrow. The starter is
 * not on the reset itself (it is never dealt or tiled), so it stands in for
 * the chapter's seventh stop here, to keep the map's seven-stop shape.
 *
 * Shown, not used: a stray tap on a stop must not open a signpost from inside
 * the intro. The one way on is the button.
 *
 * With `promise`, it is the whole landing (the `landing` experiment's `road`
 * arm, lib/experiments.ts, owner 2026-10-19): the first thing an ad visitor
 * sees, headed by the ad's own line (`door.promise`, "21 days to become
 * creative again."), with no question before it and no way back from it.
 * index.html draws this same markup before the app has loaded (a new visitor
 * has no dreams, so the goal is the default one); data/road-predraw.test.ts
 * holds the two together, so a change here means running it with
 * UPDATE_PREDRAW=1.
 */
export function FirstRoad({ promise, onGo, onBack }: { promise?: string; onGo: () => void; onBack?: () => void }) {
  const profile = loadProfile();
  const chapter = chaptersFor(profile)[0];
  const goal = goalFor(profile.dreams);
  const first = { ...getChallenge(IMAGINE)!, title: ROAD.stop };
  const stops: RoadStop[] = [
    { c: first, state: 'next' },
    ...chapter.stops.slice(0, PER_CHAPTER - 1).map((c): RoadStop => ({ c, state: 'later' })),
  ];
  const tomorrow = chapter.stops[0]?.id;
  useEffect(() => { track('intro_road', { today: IMAGINE, tomorrow, goal: goal.line }); }, [tomorrow, goal.line]);
  return (
    <Frame theme={promise ? 'fit road-landing' : 'fit'} left={<span className="tag">Day 1 · {goal.line}</span>}>
      {/* in the intro, no heading: the map is the screen (owner 2026-10-15), the one line says how to read it.
          As the landing, the ad's own line heads it, so the page is plainly the one the ad promised. */}
      {promise && <h1 className="road-landing-promise">{promise}</h1>}
      <p className="why first-road-line">{ROAD.body}</p>
      {/* as the landing, the road in depth, standing at today's stop: the rest sealed, your creative life at the end (DepthRoad, owner 2026-10-20) */}
      <div className={promise ? 'road-preview road-depth' : 'road-preview'} inert aria-hidden="true">
        {promise
          ? <DepthRoad stops={stops} camera={0} goal={goal} goalDay={ROAD.goalDay} nextLine={ROAD.today} tomorrow={ROAD.tomorrow} ahead={1} />
          : <RoadMap chapter={chapter} stops={stops} goal={goal} onStart={() => {}} onTurn={() => {}} />}
      </div>
      <div className="stack">
        <button className="btn coral" onClick={onGo}>{ROAD.done}</button>
        {onBack
          ? (
            <div className="row" style={{ justifyContent: 'center' }}>
              <button className="link" onClick={onBack}>Back</button>
            </div>
          )
          : <p className="hint road-landing-hint">{fillIn(ROAD.minutes, { n: String(first.minutes) })}</p>}
      </div>
    </Frame>
  );
}
